/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useAnimations.ts
 * PURPOSE: NQH Brand Animation System - React Hooks for Animation Control
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-01-26
 * COMPLIANCE: SDLC 6.3.1 Design-First Thinking (DFT) + NQH Brand Design System V2.1
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review + Rule 8: Quality Gates
 *
 * @business-context
 * - Domain: Vietnamese F&B Visual Identity, Premium User Experience
 * - Impact: UX Quality (6.5/10 → 8.5/10), Brand Consistency
 * - Stakeholders: End Users, Design Team, Development Team
 *
 * @technical-architecture
 * - Layer: Presentation Logic Layer (React Hooks)
 * - Pattern: Custom React Hooks, Composition, Accessibility-First
 * - Dependencies: React 18, animations.css, nqh-design-tokens.css
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - Sprint: docs/08-collaborate/06-Project-Management/SPRINT-33-EXECUTION-PLAN.md
 * - CSS: frontend/src/styles/animations.css
 * - Tokens: frontend/src/styles/nqh-design-tokens.css
 *
 * @performance-constraints (CTO Mandate)
 * - Respect prefers-reduced-motion media query
 * - Use RAF for animation callbacks
 * - Clean up animations on unmount
 *
 * DESIGN: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * APPROVED: Pending CTO Review (Sprint 33 Design Phase)
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Animation names matching CSS keyframe definitions
 */
export type AnimationName =
  // Fade animations
  | 'fadeIn'
  | 'fadeOut'
  | 'fadeInUp'
  | 'fadeInDown'
  | 'fadeOutUp'
  | 'fadeOutDown'
  // Slide animations
  | 'slideInLeft'
  | 'slideInRight'
  | 'slideOutLeft'
  | 'slideOutRight'
  | 'slideInUp'
  | 'slideInDown'
  // Scale animations
  | 'scaleIn'
  | 'scaleOut'
  | 'bounceIn'
  | 'popIn'
  // Modal animations
  | 'modalIn'
  | 'modalOut'
  | 'overlayIn'
  | 'overlayOut'
  // Loading animations
  | 'spin'
  | 'pulse'
  | 'shimmer'
  | 'bounce'
  // Interactive animations
  | 'shake'
  | 'wobble'
  // Vietnamese identity animations
  | 'coffeeSteam'
  | 'coffeePour'
  | 'herbSway';

/**
 * Animation duration tokens (matches CSS custom properties)
 */
export type AnimationDuration =
  | 'instant'   // 0ms
  | '75'        // 75ms - Micro-interactions
  | '100'       // 100ms - Button states
  | '150'       // 150ms - Default transitions
  | '200'       // 200ms - Card hovers
  | '300'       // 300ms - Page transitions (CTO MAX)
  | '500'       // 500ms - Complex animations
  | '700'       // 700ms - Loading states
  | '1000';     // 1000ms - Infinite loops

/**
 * Animation easing tokens (matches CSS custom properties)
 */
export type AnimationEasing =
  | 'linear'
  | 'in'
  | 'out'
  | 'in-out'
  | 'bounce'
  | 'spring'
  | 'coffee';   // Vietnamese coffee pour signature

/**
 * Animation configuration options
 */
export interface AnimationOptions {
  /** Animation name (keyframe) */
  animation: AnimationName;
  /** Duration token */
  duration?: AnimationDuration;
  /** Easing token */
  easing?: AnimationEasing;
  /** Animation delay in ms */
  delay?: number;
  /** Fill mode */
  fillMode?: 'none' | 'forwards' | 'backwards' | 'both';
  /** Iteration count */
  iterations?: number | 'infinite';
  /** Callback when animation ends */
  onAnimationEnd?: () => void;
}

/**
 * Staggered animation configuration
 */
export interface StaggerOptions {
  /** Base animation name */
  animation: AnimationName;
  /** Delay between each item (ms) */
  staggerDelay?: number;
  /** Duration token */
  duration?: AnimationDuration;
  /** Easing token */
  easing?: AnimationEasing;
  /** Total number of items to animate */
  itemCount: number;
}

/**
 * Animation state returned by hooks
 */
export interface AnimationState {
  /** Whether animation is currently running */
  isAnimating: boolean;
  /** Whether animation has completed */
  isComplete: boolean;
  /** Current animation progress (0-1) */
  progress: number;
}

/**
 * Return type for useAnimation hook
 */
export interface UseAnimationReturn {
  /** Ref to attach to the animated element */
  ref: React.RefObject<HTMLElement>;
  /** CSS class to apply for animation */
  animationClass: string;
  /** Trigger animation manually */
  trigger: () => void;
  /** Reset animation state */
  reset: () => void;
  /** Current animation state */
  state: AnimationState;
  /** Whether user prefers reduced motion */
  prefersReducedMotion: boolean;
}

/**
 * Return type for useStaggeredAnimation hook
 */
export interface UseStaggeredAnimationReturn {
  /** Get CSS class for specific item index */
  getItemClass: (index: number) => string;
  /** Get inline style for specific item index */
  getItemStyle: (index: number) => React.CSSProperties;
  /** Trigger all animations */
  trigger: () => void;
  /** Reset all animations */
  reset: () => void;
  /** Whether user prefers reduced motion */
  prefersReducedMotion: boolean;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Maps duration token to CSS custom property value
 */
const getDurationValue = (duration: AnimationDuration): string => {
  const durationMap: Record<AnimationDuration, string> = {
    instant: '0ms',
    '75': 'var(--nqh-duration-75)',
    '100': 'var(--nqh-duration-100)',
    '150': 'var(--nqh-duration-150)',
    '200': 'var(--nqh-duration-200)',
    '300': 'var(--nqh-duration-300)',
    '500': 'var(--nqh-duration-500)',
    '700': 'var(--nqh-duration-700)',
    '1000': 'var(--nqh-duration-1000)',
  };
  return durationMap[duration];
};

/**
 * Maps easing token to CSS custom property value
 */
const getEasingValue = (easing: AnimationEasing): string => {
  const easingMap: Record<AnimationEasing, string> = {
    linear: 'var(--nqh-ease-linear)',
    in: 'var(--nqh-ease-in)',
    out: 'var(--nqh-ease-out)',
    'in-out': 'var(--nqh-ease-in-out)',
    bounce: 'var(--nqh-ease-bounce)',
    spring: 'var(--nqh-ease-spring)',
    coffee: 'var(--nqh-ease-coffee)',
  };
  return easingMap[easing];
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to detect user's reduced motion preference
 * @returns Whether user prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return prefersReducedMotion;
}

/**
 * Main animation hook - controls single element animation
 *
 * @example
 * ```tsx
 * const { ref, animationClass, trigger } = useAnimation({
 *   animation: 'fadeInUp',
 *   duration: '300',
 *   easing: 'coffee',
 * });
 *
 * return <div ref={ref} className={animationClass}>Content</div>;
 * ```
 */
export function useAnimation(options: AnimationOptions): UseAnimationReturn {
  const {
    animation,
    duration = '300',
    easing = 'out',
    delay = 0,
    fillMode = 'forwards',
    iterations = 1,
    onAnimationEnd,
  } = options;

  const ref = useRef<HTMLElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [state, setState] = useState<AnimationState>({
    isAnimating: false,
    isComplete: false,
    progress: 0,
  });
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Build animation class
  const animationClass = useMemo(() => {
    if (prefersReducedMotion || !shouldAnimate) return '';
    return `animate-${animation}`;
  }, [animation, prefersReducedMotion, shouldAnimate]);

  // Handle animation end
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleAnimationEnd = () => {
      setState((prev) => ({
        ...prev,
        isAnimating: false,
        isComplete: true,
        progress: 1,
      }));
      onAnimationEnd?.();
    };

    element.addEventListener('animationend', handleAnimationEnd);
    return () => element.removeEventListener('animationend', handleAnimationEnd);
  }, [onAnimationEnd]);

  // Trigger animation
  const trigger = useCallback(() => {
    if (prefersReducedMotion) {
      // Skip animation, immediately complete
      setState({ isAnimating: false, isComplete: true, progress: 1 });
      onAnimationEnd?.();
      return;
    }

    // Reset and re-trigger
    setShouldAnimate(false);
    setState({ isAnimating: false, isComplete: false, progress: 0 });

    // Use RAF to ensure re-render before starting animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setShouldAnimate(true);
        setState({ isAnimating: true, isComplete: false, progress: 0 });
      });
    });
  }, [prefersReducedMotion, onAnimationEnd]);

  // Reset animation
  const reset = useCallback(() => {
    setShouldAnimate(false);
    setState({ isAnimating: false, isComplete: false, progress: 0 });
  }, []);

  // Build inline style for custom duration/easing/delay
  useEffect(() => {
    const element = ref.current;
    if (!element || prefersReducedMotion) return;

    if (shouldAnimate) {
      element.style.animationDuration = getDurationValue(duration);
      element.style.animationTimingFunction = getEasingValue(easing);
      element.style.animationDelay = `${delay}ms`;
      element.style.animationFillMode = fillMode;
      element.style.animationIterationCount =
        iterations === 'infinite' ? 'infinite' : String(iterations);
    }
  }, [shouldAnimate, duration, easing, delay, fillMode, iterations, prefersReducedMotion]);

  return {
    ref,
    animationClass,
    trigger,
    reset,
    state,
    prefersReducedMotion,
  };
}

/**
 * Hook for staggered list animations
 *
 * @example
 * ```tsx
 * const { getItemClass, getItemStyle, trigger } = useStaggeredAnimation({
 *   animation: 'fadeInUp',
 *   staggerDelay: 50,
 *   itemCount: items.length,
 * });
 *
 * return (
 *   <ul>
 *     {items.map((item, index) => (
 *       <li key={item.id} className={getItemClass(index)} style={getItemStyle(index)}>
 *         {item.name}
 *       </li>
 *     ))}
 *   </ul>
 * );
 * ```
 */
export function useStaggeredAnimation(options: StaggerOptions): UseStaggeredAnimationReturn {
  const {
    animation,
    staggerDelay = 50,
    duration = '300',
    easing = 'coffee',
    itemCount,
  } = options;

  const prefersReducedMotion = usePrefersReducedMotion();
  const [isTriggered, setIsTriggered] = useState(false);

  // Get CSS class for item
  const getItemClass = useCallback(
    (index: number): string => {
      if (prefersReducedMotion || !isTriggered) return '';
      return `animate-${animation}`;
    },
    [animation, prefersReducedMotion, isTriggered]
  );

  // Get inline style for item (animation delay)
  const getItemStyle = useCallback(
    (index: number): React.CSSProperties => {
      if (prefersReducedMotion || !isTriggered) return {};
      return {
        opacity: 0, // Start hidden
        animationDuration: getDurationValue(duration),
        animationTimingFunction: getEasingValue(easing),
        animationDelay: `${index * staggerDelay}ms`,
        animationFillMode: 'forwards',
      };
    },
    [duration, easing, staggerDelay, prefersReducedMotion, isTriggered]
  );

  // Trigger all animations
  const trigger = useCallback(() => {
    setIsTriggered(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsTriggered(true);
      });
    });
  }, []);

  // Reset all animations
  const reset = useCallback(() => {
    setIsTriggered(false);
  }, []);

  // Auto-trigger on mount
  useEffect(() => {
    trigger();
  }, [trigger]);

  return {
    getItemClass,
    getItemStyle,
    trigger,
    reset,
    prefersReducedMotion,
  };
}

/**
 * Hook for page transition animations
 *
 * @example
 * ```tsx
 * const { pageClass, isEntering, isExiting } = usePageTransition();
 *
 * return <div className={pageClass}>Page Content</div>;
 * ```
 */
export function usePageTransition(): {
  pageClass: string;
  isEntering: boolean;
  isExiting: boolean;
  triggerExit: () => Promise<void>;
  prefersReducedMotion: boolean;
} {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isEntering, setIsEntering] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  // Auto-trigger enter animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsEntering(false);
    }, 300); // Match animation duration
    return () => clearTimeout(timer);
  }, []);

  // Page class
  const pageClass = useMemo(() => {
    if (prefersReducedMotion) return '';
    if (isEntering) return 'page-enter';
    if (isExiting) return 'page-exit';
    return '';
  }, [isEntering, isExiting, prefersReducedMotion]);

  // Trigger exit animation (returns promise that resolves when done)
  const triggerExit = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (prefersReducedMotion) {
        resolve();
        return;
      }
      setIsExiting(true);
      setTimeout(() => {
        resolve();
      }, 200); // Match exit animation duration
    });
  }, [prefersReducedMotion]);

  return {
    pageClass,
    isEntering,
    isExiting,
    triggerExit,
    prefersReducedMotion,
  };
}

/**
 * Hook for hover animation effects
 *
 * @example
 * ```tsx
 * const { hoverClass, handlers } = useHoverAnimation('lift');
 *
 * return <div className={hoverClass} {...handlers}>Card</div>;
 * ```
 */
export function useHoverAnimation(
  effect: 'lift' | 'coffee-glow' | 'scale' | 'grow' = 'lift'
): {
  hoverClass: string;
  isHovered: boolean;
  handlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
  prefersReducedMotion: boolean;
} {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isHovered, setIsHovered] = useState(false);

  const effectClassMap: Record<string, string> = {
    lift: 'hover-lift',
    'coffee-glow': 'hover-coffee-glow',
    scale: 'hover-scale',
    grow: 'hover-grow',
  };

  const hoverClass = prefersReducedMotion ? '' : effectClassMap[effect] || '';

  const handlers = useMemo(
    () => ({
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      onFocus: () => setIsHovered(true),
      onBlur: () => setIsHovered(false),
    }),
    []
  );

  return {
    hoverClass,
    isHovered,
    handlers,
    prefersReducedMotion,
  };
}

/**
 * Hook for loading skeleton animation
 *
 * @example
 * ```tsx
 * const { skeletonClass } = useSkeletonAnimation();
 *
 * return <div className={skeletonClass} style={{ width: 200, height: 20 }} />;
 * ```
 */
export function useSkeletonAnimation(): {
  skeletonClass: string;
  prefersReducedMotion: boolean;
} {
  const prefersReducedMotion = usePrefersReducedMotion();

  return {
    skeletonClass: prefersReducedMotion ? '' : 'animate-shimmer',
    prefersReducedMotion,
  };
}

/**
 * Hook for modal/dialog animations
 *
 * @example
 * ```tsx
 * const { modalClass, overlayClass, isVisible, open, close } = useModalAnimation();
 *
 * return (
 *   <Dialog open={isVisible}>
 *     <div className={overlayClass} />
 *     <div className={modalClass}>Modal Content</div>
 *   </Dialog>
 * );
 * ```
 */
export function useModalAnimation(): {
  modalClass: string;
  overlayClass: string;
  isVisible: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => Promise<void>;
  prefersReducedMotion: boolean;
} {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const modalClass = useMemo(() => {
    if (prefersReducedMotion) return '';
    if (isOpen && !isClosing) return 'animate-modalIn';
    if (isClosing) return 'animate-modalOut';
    return '';
  }, [isOpen, isClosing, prefersReducedMotion]);

  const overlayClass = useMemo(() => {
    if (prefersReducedMotion) return '';
    if (isOpen && !isClosing) return 'animate-overlayIn';
    if (isClosing) return 'animate-overlayOut';
    return '';
  }, [isOpen, isClosing, prefersReducedMotion]);

  const open = useCallback(() => {
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  const close = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (prefersReducedMotion) {
        setIsOpen(false);
        resolve();
        return;
      }
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
        resolve();
      }, 200); // Match animation duration
    });
  }, [prefersReducedMotion]);

  return {
    modalClass,
    overlayClass,
    isVisible: isOpen,
    isOpen,
    open,
    close,
    prefersReducedMotion,
  };
}

/**
 * Hook for toast/notification animations
 *
 * @example
 * ```tsx
 * const { toastClass, show, hide, isVisible } = useToastAnimation();
 * ```
 */
export function useToastAnimation(): {
  toastClass: string;
  isVisible: boolean;
  show: () => void;
  hide: () => Promise<void>;
  prefersReducedMotion: boolean;
} {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const toastClass = useMemo(() => {
    if (prefersReducedMotion) return '';
    if (isVisible && !isExiting) return 'toast-enter';
    if (isExiting) return 'toast-exit';
    return '';
  }, [isVisible, isExiting, prefersReducedMotion]);

  const show = useCallback(() => {
    setIsExiting(false);
    setIsVisible(true);
  }, []);

  const hide = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (prefersReducedMotion) {
        setIsVisible(false);
        resolve();
        return;
      }
      setIsExiting(true);
      setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
        resolve();
      }, 200);
    });
  }, [prefersReducedMotion]);

  return {
    toastClass,
    isVisible,
    show,
    hide,
    prefersReducedMotion,
  };
}

/**
 * Hook for dropdown menu animations
 *
 * @example
 * ```tsx
 * const { dropdownClass, isOpen, toggle, open, close } = useDropdownAnimation();
 * ```
 */
export function useDropdownAnimation(): {
  dropdownClass: string;
  isOpen: boolean;
  isVisible: boolean;
  toggle: () => void;
  open: () => void;
  close: () => Promise<void>;
  prefersReducedMotion: boolean;
} {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const dropdownClass = useMemo(() => {
    if (prefersReducedMotion) return '';
    if (isOpen && !isClosing) return 'dropdown-enter';
    if (isClosing) return 'dropdown-exit';
    return '';
  }, [isOpen, isClosing, prefersReducedMotion]);

  const open = useCallback(() => {
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  const close = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (prefersReducedMotion) {
        setIsOpen(false);
        resolve();
        return;
      }
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
        resolve();
      }, 150);
    });
  }, [prefersReducedMotion]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  return {
    dropdownClass,
    isOpen,
    isVisible: isOpen || isClosing,
    toggle,
    open,
    close,
    prefersReducedMotion,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  useAnimation,
  useStaggeredAnimation,
  usePageTransition,
  useHoverAnimation,
  useSkeletonAnimation,
  useModalAnimation,
  useToastAnimation,
  useDropdownAnimation,
  usePrefersReducedMotion,
};
